/**
 * Pan and zoom of the terrain map: the whole transform as three numbers, and
 * the conversions that go through it. Kept pure and apart from the component
 * so the screen to world conversion the designer and the direction page place
 * objectives with is proved by a unit test (mapView.test.ts), and so the map
 * stays dependency-free: a CSS transform on a wrapper, nothing more.
 */
export interface MapView {
  /** 1 fits the whole terrain in the box; MAX_SCALE is as close as it goes. */
  scale: number;
  /** Where the scaled image's top left corner sits, in box pixels. */
  x: number;
  y: number;
}

/** The whole terrain, which is where every map opens. */
export const FIT_VIEW: MapView = { scale: 1, x: 0, y: 0 };
export const MIN_SCALE = 1;
/**
 * ×24 on Altis is 1.3 km across a 620 px square: a village's sectors, 60 to
 * 200 m blocks, read at that zoom and were a thumb's width at the old ×12.
 */
export const MAX_SCALE = 24;
/** One press of the + or − button. */
export const STEP = 1.6;

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

/**
 * Zoom within range and no empty margin at any edge, whatever the pan asked for.
 *
 * The terrain is drawn on a square canvas `size` wide; the box it is seen through is as wide
 * as that canvas and may be less tall (`height`, the canvas's own side when left out). A box
 * wider than tall shows a band of the square, and the pan reaches the rest: nothing else in
 * the conversions changes, because they all go through the canvas.
 */
export function clampView(view: MapView, size: number, height: number = size): MapView {
  const scale = clamp(view.scale, MIN_SCALE, MAX_SCALE);
  return {
    scale,
    x: clamp(view.x, size - size * scale, 0),
    y: clamp(view.y, Math.min(height, size) - size * scale, 0),
  };
}

/** The whole width of the terrain with the box on the middle of it: where a box wider than tall opens. */
export const centredView = (size: number, height: number = size): MapView =>
  clampView({ scale: 1, x: 0, y: (height - size) / 2 }, size, height);

/** Zoom by `factor` leaving whatever is under (ox, oy), in box pixels, where it is. */
export function zoomAt(
  view: MapView,
  factor: number,
  ox: number,
  oy: number,
  size: number,
  height: number = size,
): MapView {
  const scale = clamp(view.scale * factor, MIN_SCALE, MAX_SCALE);
  const k = scale / view.scale;
  return clampView({ scale, x: ox - (ox - view.x) * k, y: oy - (oy - view.y) * k }, size, height);
}

/** Pan by a drag of (dx, dy) box pixels. */
export const panBy = (
  view: MapView,
  dx: number,
  dy: number,
  size: number,
  height: number = size,
): MapView => clampView({ scale: view.scale, x: view.x + dx, y: view.y + dy }, size, height);

/**
 * Box pixels (clientX minus the box's left) to world metres, through the
 * current transform: this is what click-to-place sends to the runtime, so a
 * zoomed or panned map must place the objective where the pointer is. World y
 * grows north and the image grows down, hence the flip.
 */
export function screenToMap(
  ox: number,
  oy: number,
  view: MapView,
  size: number,
  worldSize: number,
): [number, number] {
  const px = (ox - view.x) / view.scale;
  const py = (oy - view.y) / view.scale;
  return [Math.round((px / size) * worldSize), Math.round(((size - py) / size) * worldSize)];
}

/**
 * The square the Atlas's tiles fill, in canvas pixels: its width and the offset of its top edge.
 * The pyramid starts at the terrain's south-west corner and is a little wider than the terrain
 * (`tileExtentInMeters`, 30 787.9 m for Altis's 30 720 m), so laid over the terrain's own edge it
 * stretched the ground by up to half a percent and every marker stood up to 68 m south-west of
 * where its place is on the picture. With no extent given the square is the terrain's measure.
 */
export function tileSquare(
  map: { sizeInMeters: number; tileExtentInMeters?: number },
  worldSize: number,
  size: number,
): { span: number; top: number } {
  const metres =
    map.tileExtentInMeters && map.tileExtentInMeters > 0
      ? map.tileExtentInMeters
      : map.sizeInMeters;
  const span = metres > 0 && worldSize > 0 ? (metres / worldSize) * size : size;
  return { span, top: size - span };
}

/** A box in world metres: what the map is asked to frame. */
export interface WorldBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/**
 * The view that frames a box of the world — the area of operations — in the
 * square, with a margin round it, as close as the range allows and never past
 * the terrain's edge (`clampView`). This is where a mission's map opens when it
 * has something to look at closer than the whole island: a village cut into
 * sectors is three pixels wide at the terrain zoom and nobody could see it
 * (the owner, 2026-10-11). `margin` is a share of the box's longer side left
 * on each side of it.
 */
export function fitBounds(
  bounds: WorldBounds,
  size: number,
  worldSize: number,
  margin = 0.1,
  height: number = size,
): MapView {
  if (!(size > 0) || !(worldSize > 0) || !(height > 0)) return FIT_VIEW;
  const w = ((bounds.maxX - bounds.minX) / worldSize) * size;
  const h = ((bounds.maxY - bounds.minY) / worldSize) * size;
  // The margin is the same on every side; in a square box this is the longer side filling it.
  const pad = 2 * margin * Math.max(w, h);
  const fits = Math.max(w, h) > 0 ? Math.min(size / (w + pad), height / (h + pad)) : MAX_SCALE;
  const scale = clamp(fits, MIN_SCALE, MAX_SCALE);
  const cx = (((bounds.minX + bounds.maxX) / 2) * size) / worldSize;
  const cy = size - (((bounds.minY + bounds.maxY) / 2) * size) / worldSize;
  return clampView({ scale, x: size / 2 - cx * scale, y: height / 2 - cy * scale }, size, height);
}

/** Whether two views are the same to the eye: the fit button reads this to know where it stands. */
export const sameView = (a: MapView, b: MapView): boolean =>
  Math.abs(a.scale - b.scale) < 1e-3 && Math.abs(a.x - b.x) < 0.5 && Math.abs(a.y - b.y) < 0.5;

/** World metres to box pixels: the inverse of screenToMap, and how a marker is placed. */
export function mapToScreen(
  x: number,
  y: number,
  view: MapView,
  size: number,
  worldSize: number,
): [number, number] {
  const px = (x / worldSize) * size;
  const py = size - (y / worldSize) * size;
  return [px * view.scale + view.x, py * view.scale + view.y];
}
